import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sunrise, Scale, Moon } from 'lucide-react';
import { toast } from 'sonner';
import { nexaApi } from './api';

const SleepQuestion = ({ onSaved }) => {
  const [feel, setFeel] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!feel || busy) return;
    setBusy(true);
    try {
      await nexaApi.post('/sleep-note', { feel, note: note.trim() || null });
      toast.success('Sleep noted');
      onSaved();
    } catch {
      toast.error('Could not save');
      setBusy(false);
    }
  };
  return (
    <div className="px-5 py-4" data-testid="nexa-morning-sleep">
      <div className="flex items-center gap-2 mb-3">
        <Moon className="w-3.5 h-3.5 text-sky-400" strokeWidth={1.8} />
        <span className="text-sm text-white/85">How do you feel after sleep?</span>
      </div>
      <div className="flex gap-1.5 flex-wrap">
        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            onClick={() => setFeel(n)}
            data-testid={`nexa-sleep-feel-${n}`}
            className={`w-8 h-9 rounded-lg text-sm font-semibold border transition-colors ${
              feel === n ? 'bg-sky-400 text-black border-sky-400' : 'border-white/15 text-white/60'
            }`}
          >
            {n}
          </button>
        ))}
      </div>
      <div className="flex gap-2 mt-3">
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional: snored, woke twice…"
          data-testid="nexa-sleep-note-input"
          className="nexa-input flex-1 h-11 rounded-xl px-4 text-sm"
        />
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={save}
          disabled={!feel || busy}
          data-testid="nexa-sleep-feel-save"
          className="h-11 px-5 rounded-xl bg-white text-black font-semibold text-sm disabled:opacity-40"
        >
          Save
        </motion.button>
      </div>
    </div>
  );
};

const WeightQuestion = ({ latest, onSaved }) => {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const num = parseFloat(value);
  const valid = !Number.isNaN(num) && num > 20 && num < 400;
  const save = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await nexaApi.post('/weight', { weight: num });
      toast.success(`Weight logged — ${num} kg`);
      onSaved();
    } catch {
      toast.error('Could not save');
      setBusy(false);
    }
  };
  return (
    <div className="px-5 py-4" data-testid="nexa-morning-weight">
      <div className="flex items-center gap-2 mb-3">
        <Scale className="w-3.5 h-3.5 text-sky-400" strokeWidth={1.8} />
        <span className="text-sm text-white/85">What&apos;s your weight this morning?</span>
      </div>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <input
            type="number"
            inputMode="decimal"
            step="0.1"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && save()}
            placeholder={latest ? `Last: ${latest} kg` : 'e.g. 84.5'}
            data-testid="nexa-weight-input"
            className="nexa-input w-full h-11 rounded-xl px-4 pr-10 text-sm"
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-white/35 font-mono">kg</span>
        </div>
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={save}
          disabled={!valid || busy}
          data-testid="nexa-weight-save"
          className="h-11 px-5 rounded-xl bg-white text-black font-semibold text-sm disabled:opacity-40"
        >
          Save
        </motion.button>
      </div>
    </div>
  );
};

export const MorningCheckin = ({ data, onSaved }) => {
  const h = new Date().getHours();
  if (h < 6 || h >= 12) return null;
  const needSleep = !(data.health && data.health.sleep_feel);
  const needWeight = data.weight_today == null;
  const pending = [needSleep, needWeight].filter(Boolean).length;
  if (!pending) return null;
  return (
    <div data-testid="nexa-morning-checkin">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sunrise className="w-3.5 h-3.5 text-amber-400" strokeWidth={1.8} />
          <span className="text-xs tracking-[0.2em] uppercase text-white/40">Morning check-in</span>
        </div>
        <span className="text-[10px] font-mono text-amber-400/70 tracking-widest" data-testid="nexa-morning-pending">
          {pending} LEFT
        </span>
      </div>
      <div className="rounded-3xl border border-amber-400/20 bg-[#0A0A0A] overflow-hidden divide-y divide-white/[0.06]">
        <AnimatePresence initial={false}>
          {needSleep && (
            <motion.div key="sleep" exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <SleepQuestion onSaved={onSaved} />
            </motion.div>
          )}
          {needWeight && (
            <motion.div key="weight" exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <WeightQuestion latest={data.weight_latest} onSaved={onSaved} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
